import React, { useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { useNavigate, useParams, Link } from "react-router-dom";
import useAutoUpdateInitialValues from "hooks/useAutoUpdateInitialValues";
import { SCHOOL_LIST } from "config/paths";
import { useCreateSchoolMutation, useGetSchoolDataQuery, useLazyGetSchoolListQuery, useUpdateSchoolMutation } from "store/services/schools";
import { Formik } from "formik";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import schoolFormSchema from "./School.schema";
import { useLazyGetSchoolDivisonListQuery, useSchoolDivisonLookupQuery } from "store/services/schoolsDivisons";
import { cloneDeep, debounce } from "lodash";
import AsyncSelect from "react-select/async";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import LoadingButton from "components/shared/LoadingButton";
import { successMessageToast } from "helpers/common";
import ReactSelect from "react-select";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";

const AddSchool = () => {
  const { id: schoolId } = useParams();
  const editMode = !!schoolId;
  const navigate = useNavigate();

  const { data: schoolDivisonsList, refetch: refetchSchoolDivision } = useSchoolDivisonLookupQuery();
  const schoolDivisonsOptions = schoolDivisonsList?.map(division => ({ value: division.id, label: division.title })) || [];
  const { data: schoolData, isLoading: getSchoolLoading, isError: getSchoolError } = useGetSchoolDataQuery(schoolId, { skip: !schoolId });
  const [createSchool, { isLoading }] = useCreateSchoolMutation();
  const [updateSchool, { isLoading: updateSchoolLoading }] = useUpdateSchoolMutation();

  const [initialValues] = useAutoUpdateInitialValues(defaultInitialValues, [schoolData]);
  initialValues.division_id = schoolDivisonsOptions?.find(x => x.value == schoolData?.division_id) || null;

  const handleClickCancel = () => {
    navigate(SCHOOL_LIST);
  }

  const handleSubmit = async (payload) => {
    try {
      let result;
      let values = cloneDeep(payload);

      // transform values
      values.division_id = values?.division_id?.value;

      if (editMode) {
        result = await updateSchool({
          id: schoolId,
          payload: values
        });
      } else {
        result = await createSchool(values);
      }
      if (result.error) {
        console.error("Error creating discipline", result.error);
      } else {
        successMessageToast(result.data?.message || "Discipline created successfully");
        navigate(SCHOOL_LIST);
      }
    } catch (err) {
      console.log("Error creating discipline", err);
    }
  }

  useEffect(() => {
    refetchSchoolDivision();
  }, [])
  return (
    <>
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/manage-schools/school-profiles/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit School" : "Add New School"}</h6>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              {getSchoolLoading ? (
                <LoadingIndicator />
              ) : getSchoolError ? (
                <ErrorComponent />
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={schoolFormSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div
                        className="py-3"
                        style={{ borderTop: "1px solid #41a2a84d" }}
                      >
                        <Row>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              School Division
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="division_id"
                              placeholder={"Please Select Division"}
                              option={schoolDivisonsOptions}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              School Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="name"
                              placeholder="Enter School Name"
                              className={`${styles.formControl} form-control`}
                              maxLength={100}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Location
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="location"
                              placeholder="Enter Location"
                              className={`${styles.formControl} form-control`}
                              maxLength={50}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Contact Person Last Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="contact_last_name"
                              placeholder="e.g Smith"
                              className={`${styles.formControl} form-control`}
                              maxLength={30}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Contact Person First Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="contact_first_name"
                              placeholder="e.g John"
                              className={`${styles.formControl} form-control`}
                              maxLength={30}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Contact Title
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="contact_title"
                              placeholder="e.g Mr."
                              className={`${styles.formControl} form-control`}
                              maxLength={50}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0n d-flex"
                            >
                              Email
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="email"
                              placeholder="Email"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0n d-flex"
                            >
                              Phone Number
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="phone_number"
                              type="tel"
                              placeholder="e.g +1 2554 888"
                              className={`${styles.formControl} form-control`}
                              maxLength={25}
                            />
                          </Col>
                          <Col md="8" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Description
                            </label>
                            <AppInputField
                              name="description"
                              fieldType={"textarea"}
                              placeholder="Please Add Description"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                        </Row>
                      </div>
                      <Row className="mt-4">
                        <Col className="my-2" lg="12">
                          <div
                            className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                            style={{ borderTop: "1px solid #41a2a84d" }}
                          >
                            <LoadingButton
                              type="submit"
                              className="d-flex align-items-center justify-content-center commonBtn"
                              isLoading={isLoading || updateSchoolLoading}
                            >
                              Save
                            </LoadingButton>
                            <Button
                              className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                              onClick={handleClickCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

const defaultInitialValues = {
  division_id: null,
  name: "",
  location: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_title: "",
  email: "",
  phone_number: "",
  description: "",
  status: "active"
}

export default AddSchool;
